import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, CardMedia, Box, Chip } from '@material-ui/core';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import { Query } from 'react-apollo';
import gql from 'graphql-tag';

const useStyles = makeStyles(theme => ({
  card: {
    width: '100%',
    overflow: 'hidden', // Clip fanned photos at card boundary
    position: 'relative',
    // No border, shadow, background, or hover effects
  },
  mediaContainer: {
    position: 'relative',
    height: 360,
    overflow: 'hidden', // Changed to hidden to clip fanned photos at card boundary
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[100],
  },
  photoFanContainer: {
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    perspective: 1000,
  },
  photoFanStack: {
    position: 'absolute',
    width: '70%', // Further reduced to bring photos in and prevent clipping
    height: '70%', // Further reduced to bring photos in and prevent clipping
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  fanPhoto: {
    position: 'absolute',
    width: 'auto',
    height: '45%', // Further reduced to prevent clipping at edges
    maxWidth: '45%', // Further reduced to prevent clipping at edges
    maxHeight: '45%', // Further reduced to prevent clipping at edges
    borderRadius: theme.shape.borderRadius,
    objectFit: 'cover', // Cover to fill space nicely
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.3s ease, opacity 0.3s ease',
    border: `2px solid ${theme.palette.common.white}`,
    backgroundColor: theme.palette.common.white,
    left: '50%',
    top: '50%',
    transformOrigin: 'center center',
    cursor: 'pointer',
  },
  stripPhoto: {
    position: 'absolute',
    // Smaller by default, scales up on hover (reduced by 15% from previous 70%)
    height: '59.5%', // 70% * 0.85 = 59.5% (15% smaller)
    width: 'auto', // Width will be calculated to maintain 2:3 aspect ratio
    maxWidth: '63.75%', // 75% * 0.85 = 63.75% (15% smaller)
    maxHeight: '59.5%', // 70% * 0.85 = 59.5% (15% smaller)
    borderRadius: theme.shape.borderRadius,
    objectFit: 'contain', // Preserve 2:3 aspect ratio (width:height = 2:3, meaning wider than tall)
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2), 0 8px 32px rgba(0, 0, 0, 0.15)',
    zIndex: 20,
    border: `3px solid ${theme.palette.common.white}`,
    backgroundColor: theme.palette.common.white,
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    left: '50%',
    top: '50%',
    transformOrigin: 'center center',
    cursor: 'pointer',
    // Scale up on hover (handled in component)
  },
  media: {
    height: '100%',
    backgroundSize: 'cover',
    transition: 'transform 0.3s ease',
    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
  titleContainer: {
    position: 'absolute',
    bottom: 0, // Moved to bottom
    left: 0,
    width: '100%',
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    zIndex: 15,
    // No background - flowing on same surface as photos
    pointerEvents: 'none',
  },
  title: {
    color: theme.palette.text.primary,
    fontWeight: 500,
    fontSize: '0.875rem',
  },
  photoCount: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 500,
    fontSize: '0.75rem',
    marginRight: theme.spacing(4),
  },
  messageContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    padding: theme.spacing(2),
    zIndex: 15,
    pointerEvents: 'none',
  },
  message: {
    width: '100%',
    objectFit: 'contain',
    maxHeight: '150px',
  },
  cardActions: {
    padding: theme.spacing(1, 2),
    borderTop: 'none', // Remove border
    backgroundColor: 'transparent', // Remove background
  },
  actionButton: {
    marginRight: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
  },
}));

// Query to fetch full_urls for specific photo IDs when needed
const GET_PHOTOS_FULL_URLS = gql`
  query GetPhotosFullUrls($photoIds: [Int!]!) {
    photos(where: { id: { _in: $photoIds } }) {
      id
      full_url
      thumb_url
    }
  }
`;

/**
 * A showcase card component for displaying photo sessions
 */
function ShowcaseCard(props) {
  const { photos = [], strip, title = '' } = props;
  const cls = useStyles();

  const [isOpen, setIsOpen] = useState(false);
  const [isHoveringStrip, setIsHoveringStrip] = useState(false); // Track hover on strip specifically
  const [photoIndex, setPhotoIndex] = useState(0);
  const [lightboxPhotos, setLightboxPhotos] = useState(/** @type {string[]} */ ([]));
  const [lightboxPhotoObjects, setLightboxPhotoObjects] = useState(/** @type {Array<any>} */ ([])); // Store photo objects for querying
  const [stripImageUrl, setStripImageUrl] = useState(/** @type {string | null} */ (null)); // Track strip image URL (full_url preferred)

  // Build initial lightbox photos list (using thumb_url if full_url not available)
  // Exclude strip from lightbox - only show regular photos
  const updateLightboxPhotos = useCallback(() => {
    if (!photos || photos.length === 0) {
      // Don't show strip in lightbox even if there are no photos
      setLightboxPhotos([]);
      setLightboxPhotoObjects([]);
      return;
    }

    // Store photo objects for querying (excluding strip from lightbox)
    const allPhotos = photos; // Don't include strip
    setLightboxPhotoObjects(allPhotos);

    // Use thumb_url as fallback if full_url not available
    // Exclude strip from lightbox - only show regular photos
    const photoUrls = photos.map(photo => photo && (photo.full_url || photo.thumb_url)).filter(Boolean);
    // Don't add strip to lightbox

    setLightboxPhotos(photoUrls);
  }, [photos]); // Removed 'strip' dependency since we don't include it in lightbox

  // Run only when photos or strip changes
  useEffect(() => {
    updateLightboxPhotos();
  }, [updateLightboxPhotos]);

  // Load full URL for strip photo if needed
  useEffect(() => {
    if (!strip) {
      setStripImageUrl(null);
      return;
    }

    // If strip already has full_url and it's not a thumbnail, use it
    if (strip.full_url && !strip.full_url.includes('/Th/') && !strip.full_url.includes('-Th.jpg')) {
      setStripImageUrl(strip.full_url);
      return;
    }

    // If strip has thumb_url but no full_url, or full_url is a thumbnail, set thumb_url initially
    if (strip.thumb_url) {
      setStripImageUrl(strip.thumb_url);
    }

    // If strip has an ID but is missing full_url, we'll query for it (handled below)
  }, [strip]);

  // Determine if we need to query for strip's full URL
  const stripNeedsFullUrl =
    strip && strip.id && (!strip.full_url || strip.full_url.includes('/Th/') || strip.full_url.includes('-Th.jpg'));

  // Callback to update strip image URL with full URL
  const updateStripWithFullUrl = useCallback(
    photoData => {
      if (!photoData || !photoData.photos || !strip || !strip.id) return;

      // Find the strip photo in the response
      const stripPhotoData = photoData.photos.find(photo => {
        const photoId = typeof photo.id === 'string' ? parseInt(photo.id, 10) : photo.id;
        const stripId = typeof strip.id === 'string' ? parseInt(strip.id, 10) : strip.id;
        return photoId === stripId && photo.full_url;
      });

      if (stripPhotoData && stripPhotoData.full_url) {
        setStripImageUrl(stripPhotoData.full_url);
      }
    },
    [strip]
  );

  // Update lightbox with full URLs once query completes
  const updateWithFullUrls = useCallback(
    photoData => {
      if (!photoData || !photoData.photos) return;

      // Create a map of photo IDs to full_urls (normalize IDs to numbers for comparison)
      const fullUrlMap = new Map();
      photoData.photos.forEach(photo => {
        if (photo.id && photo.full_url) {
          // Normalize ID to number for consistent comparison
          const id = typeof photo.id === 'string' ? parseInt(photo.id, 10) : photo.id;
          if (!isNaN(id) && id > 0) {
            fullUrlMap.set(id, photo.full_url);
            // Also store with string key for flexibility
            fullUrlMap.set(String(id), photo.full_url);
          }
        }
      });

      // Update images array with full URLs where available
      const updatedImages = lightboxPhotoObjects
        .map(photo => {
          if (!photo || !photo.id) {
            // For non-photo objects (like strip), keep existing URL
            return photo && (photo.full_url || photo.thumb_url);
          }

          // Normalize photo ID for comparison
          const photoId = typeof photo.id === 'string' ? parseInt(photo.id, 10) : photo.id;

          // PRIORITY: Check if we have a full URL from query for this photo ID
          if (!isNaN(photoId) && photoId > 0 && (fullUrlMap.has(photoId) || fullUrlMap.has(String(photoId)))) {
            const fullUrl = fullUrlMap.get(photoId) || fullUrlMap.get(String(photoId));
            // Verify it's actually a full URL (not a thumbnail)
            if (fullUrl && !fullUrl.includes('/Th/') && !fullUrl.includes('-Th.jpg')) {
              return fullUrl; // Use the full URL from query
            }
          }

          // Fallback: use existing URL
          const currentUrl = photo.full_url || photo.thumb_url;
          // Even if it's a thumbnail, return it (will be replaced when query completes)
          return currentUrl;
        })
        .filter(Boolean);

      setLightboxPhotos(updatedImages);
    },
    [lightboxPhotoObjects]
  );

  const showLightbox = () => {
    setPhotoIndex(0);
    setIsOpen(true);
    // Trigger query immediately when lightbox opens
  };

  // Extract photo IDs that need full URLs - calculate when lightbox is open
  // Note: ensurePhotoUrl in RecentSessionsPage sets full_url to thumb_url as fallback,
  // so we need to check if full_url is actually a thumbnail URL
  const photoIdsForQuery =
    isOpen && photos.length > 0
      ? photos
          .filter(p => {
            // Photo must have an ID
            if (!p.id || p.id == null) return false;

            // Check if full_url is missing, null, empty, OR if it's actually a thumbnail URL
            const fullUrl = p.full_url || '';
            const isThumbnailUrl = fullUrl.includes('/Th/') || fullUrl.includes('-Th.jpg');
            const missingFullUrl = !fullUrl || fullUrl === '' || fullUrl === null || isThumbnailUrl;

            return missingFullUrl;
          })
          .map(p => {
            // Ensure ID is a number (GraphQL expects Int!)
            const id = typeof p.id === 'string' ? parseInt(p.id, 10) : p.id;
            return id;
          })
          .filter(id => !isNaN(id) && id > 0) // Filter out invalid IDs
      : [];

  // Determine if we need to query for full URLs - always query when lightbox opens if photos need URLs
  const shouldQueryFullUrls = isOpen && photoIdsForQuery.length > 0;

  // Force query when lightbox opens - ensure full URLs are loaded immediately
  useEffect(() => {
    if (isOpen && photos.length > 0) {
      // The query will be triggered via shouldQueryFullUrls when isOpen becomes true
      // This effect ensures we react when lightbox opens
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only depend on isOpen to trigger check when lightbox opens

  const imgSrc =
    photos.length > 0 ? photos[0].full_url || photos[0].thumb_url : strip && (strip.full_url || strip.thumb_url);

  return (
    <>
      {/* Query for full URLs when lightbox opens and photos are missing full_url */}
      {shouldQueryFullUrls && (
        <Query
          query={GET_PHOTOS_FULL_URLS}
          variables={{ photoIds: photoIdsForQuery }}
          fetchPolicy='network-only'
          onCompleted={updateWithFullUrls}
        >
          {() => null}
        </Query>
      )}

      {isOpen && lightboxPhotos.length > 0 && (
        <Lightbox
          mainSrc={lightboxPhotos[photoIndex]}
          nextSrc={lightboxPhotos[(photoIndex + 1) % lightboxPhotos.length]}
          prevSrc={lightboxPhotos[(photoIndex + lightboxPhotos.length - 1) % lightboxPhotos.length]}
          onCloseRequest={() => {
            setIsOpen(false);
          }}
          onMovePrevRequest={() => setPhotoIndex((photoIndex + lightboxPhotos.length - 1) % lightboxPhotos.length)}
          onMoveNextRequest={() => setPhotoIndex((photoIndex + 1) % lightboxPhotos.length)}
          reactModalStyle={{
            overlay: {
              zIndex: 1000000,
            },
            content: {
              zIndex: 1000000,
            },
          }}
        />
      )}
      <Box className={cls.card}>
        <Box className={cls.mediaContainer} onClick={showLightbox}>
          {/* Fan-out photo stack */}
          {photos.length > 0 && (
            <Box className={cls.photoFanContainer}>
              <Box className={cls.photoFanStack}>
                {photos.slice(0, 4).map((photo, index) => {
                  // Limit to 4 photos for visual clarity
                  const photoUrl = photo.full_url || photo.thumb_url || '';
                  if (!photoUrl) return null;

                  // Calculate rotation angles for fan effect (further reduced angles to prevent clipping)
                  const totalPhotos = Math.min(photos.length, 4);
                  const rotationStep = totalPhotos > 1 ? 30 / (totalPhotos - 1) : 0; // Further reduced from 40 to 30 degrees
                  const rotation = -15 + index * rotationStep; // Further reduced from -20 to -15 degrees

                  // Offset for depth - bring photos in tighter to prevent clipping at card edges
                  const angleRad = (rotation * Math.PI) / 180;

                  // Further reduced horizontal spread to bring photos in and prevent clipping
                  const horizontalSpread = (index - (totalPhotos - 1) / 2) * 50; // Reduced from 80px to 50px to bring photos in more
                  const offsetX = horizontalSpread; // Horizontal offset - more constrained
                  const offsetY = Math.sin(angleRad) * 20 + (index - 1) * 8; // Further reduced vertical stagger

                  // Z-index decreases for photos further back (lower than strip photo)
                  const zIndex = 5 - index;

                  return (
                    <img
                      key={photo.id || index}
                      src={photoUrl}
                      alt={`Session ${index + 1}`}
                      className={cls.fanPhoto}
                      style={{
                        transform: `translate(calc(-50% + ${offsetX}px), calc(-50% + ${offsetY}px)) rotate(${rotation}deg)`,
                        zIndex: zIndex,
                        opacity: 0.9 - index * 0.15, // More pronounced fade for depth
                      }}
                    />
                  );
                })}
              </Box>

              {/* Strip photo on top - use full_url for full resolution */}
              {strip && stripImageUrl && (
                <>
                  {/* Query for strip's full URL if needed */}
                  {stripNeedsFullUrl && strip.id && (
                    <Query
                      query={GET_PHOTOS_FULL_URLS}
                      variables={{
                        photoIds: [typeof strip.id === 'string' ? parseInt(strip.id, 10) : strip.id].filter(
                          id => !isNaN(id) && id > 0
                        ),
                      }}
                      fetchPolicy='network-only'
                      onCompleted={updateStripWithFullUrl}
                    >
                      {() => null}
                    </Query>
                  )}
                  <img
                    src={stripImageUrl}
                    alt='Session strip'
                    className={cls.stripPhoto}
                    onMouseEnter={() => setIsHoveringStrip(true)}
                    onMouseLeave={() => setIsHoveringStrip(false)}
                    style={{
                      transform: isHoveringStrip
                        ? 'translate(-50%, -50%) scale(1.08)'
                        : 'translate(-50%, -50%) scale(1)',
                    }}
                  />
                </>
              )}
            </Box>
          )}

          {/* Fallback to single image if no photos */}
          {photos.length === 0 && imgSrc && <CardMedia className={cls.media} image={imgSrc} title={title} />}

          {/* Message display removed per user request */}

          <Box className={cls.titleContainer}>
            {title && (
              <Typography variant='subtitle1' className={cls.title}>
                {title}
              </Typography>
            )}
            {photos.length > 0 && (
              <Chip
                size='small'
                label={`${photos.length} photo${photos.length !== 1 ? 's' : ''}`}
                className={cls.photoCount}
              />
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}

ShowcaseCard.propTypes = {
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      full_url: PropTypes.string.isRequired,
    })
  ),
  message: PropTypes.shape({
    full_url: PropTypes.string.isRequired,
  }),
  strip: PropTypes.shape({
    full_url: PropTypes.string.isRequired,
  }),
  title: PropTypes.string,
  caption: PropTypes.string,
};

export default ShowcaseCard;
